import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AuthorizationService,
  BaseDirective,
  LoggerService,
} from '@egym/ngx-adm-int';
import {
  BehaviorSubject,
  Observable,
  Observer,
  of,
  Subscription,
  tap,
} from 'rxjs';
import { Gym, GymType, hasGymTypesChanged } from 'src/app/model/gym';
import { NetworkChangeConfirmDialogComponent } from './network-change-confirm-dialog/network-change-confirm-dialog.component';
import { NetworkChangeDialogData } from './network-change-confirm-dialog/network-change-dialog-data';
import { StudioType } from 'src/app/model/studio-type';
import { GymFinderService } from 'src/app/service/gymfinder.service';
import { GymUtils } from 'src/app/util/gym-util';
import { OpeningHourUtil } from 'src/app/util/opening-hour-util';
import {
  GymDetailsJsonDialogComponent,
  GymDetailsJsonDialogData,
} from './gym-details-json-dialog/gym-details-json-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { isValidUUID } from '../../util/uuiid-utils';
import { GymFinderServiceV2 } from '../../service/gymfinderv2.service';
import { GymUpdate } from 'src/app/model/gym-update';
import { ClassBookingConfig } from '../../model/class-booking-config';
import { NetworkService } from 'src/app/service/network.service';
import { FeatureFlags } from '../../model/feature-flags';

type TabData = {
  internalRoute: string;
  route: string;
  label: string;
};

enum InternalRoutes {
  DETAILS = 'details',
  IMAGES = 'images',
  CLASS_BOOKING = 'class-booking',
  CHECKIN_ATTEMPTS = 'checkin-attempts',
}

@Component({
  selector: 'gymfinder-gym',
  templateUrl: './gym.component.html',
  styleUrls: ['./gym.component.scss'],
  standalone: false,
})
export class GymComponent
  extends BaseDirective<Gym>
  implements OnInit, OnDestroy
{
  @ViewChild('gymForm')
  gymForm?: NgForm;

  private routeSubscription: Subscription;

  private routeUrlSubscription: Subscription;

  internalRoute: string = 'details';

  gymId: string;

  gym: GymUpdate;

  loading = false;

  updatingImages = false;

  gymAlias?: string;

  editMode = false;

  studioTypes: StudioType[];

  gymSavedSubject = new BehaviorSubject<boolean>(false);

  displayGymlibBackOfficeLink: boolean;

  tabs: TabData[] = [];

  creditsFeatureFlag = false;

  private static readonly PROPAGATION_WINDOW_MS = 60 * 60 * 1000;

  gymUpdatedAt?: Date;

  expectedLiveAt?: Date;

  isPropagationWindowElapsed = false;

  private originalGym: GymUpdate;

  constructor(
    snackBar: MatSnackBar,
    private readonly logger: LoggerService,
    private readonly route: ActivatedRoute,
    private readonly gymFinderService: GymFinderService,
    private readonly gymFinderServiceV2: GymFinderServiceV2,
    private readonly authorizationService: AuthorizationService,
    private readonly networkService: NetworkService,
    private readonly dialog: MatDialog,
    private readonly router: Router
  ) {
    super(snackBar);
    this.changeGymIdToUUID = this.changeGymIdToUUID.bind(this);
  }

  ngOnInit(): void {
    this.loading = true;

    const routeSnapshot = this.route.snapshot;

    this.gymId = routeSnapshot.params.id;

    this.loadCreditsFeatureFlag();
    this.loadGym();
    this.loadStudioTypes();

    this.routeUrlSubscription = this.route.url.subscribe(urlSegments => {
      this.internalRoute = urlSegments[urlSegments.length - 1]?.path;
    });

    this.editMode =
      this.authorizationService.checkPermission('editGymfinderGym');

    this.initTabs(this.gymId);
  }

  ngOnDestroy() {
    if (this.routeSubscription && !this.routeSubscription.closed) {
      this.routeSubscription.unsubscribe();
    }
    if (this.routeUrlSubscription && !this.routeUrlSubscription.closed) {
      this.routeUrlSubscription.unsubscribe();
    }
  }

  public onGymChange(newGym: Gym) {
    this.error = undefined;
    this.logger.info('New Gym received: ', newGym);
    this.gym = newGym;
    this.loadGym();
    this.changeGymIdToUUID(this.gym);
  }

  loadCreditsFeatureFlag() {
    this.gymFinderService.getFeatureFlags().subscribe({
      next: (value: FeatureFlags) => {
        this.creditsFeatureFlag = value.credits;
      },
      error: () => {
        this.openSnackBar('Error while getting the feature flags');
      },
    });
  }

  loadGym() {
    const getGymObserver: Observer<Gym> = {
      next: (value: Gym) => {
        this.gym = GymUtils.validateAdditionalInfoForGym(value);
        if (!this.gym.gymImages) this.gym.gymImages = [];
        if (this.gym.gymImages?.length > 0) {
          this.gym.mainImageId = this.gym.gymImages[0].imageId;
        }
        this.gymAlias = value.alias;
        this.networkService.setNetwork(value.network ?? null);
        this.originalGym = structuredClone(this.gym);
        this.logger.debug('This is the gym: ', this.gym);
        this.updatePropagationInfo();
      },
      error: (error: HttpErrorResponse) => {
        if (error.status !== 404) {
          this.error = error;
          this.logger.error('Could not load the gym: ', error);
          this.openSnackBar('Could not load the gym!');
        }
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      },
    };

    this.getGym(this.gymId).subscribe(getGymObserver);
  }

  private getGym(gymId: string): Observable<Gym> {
    if (isValidUUID(gymId)) {
      return this.gymFinderServiceV2.getGym(this.gymId);
    } else if (!isNaN(+this.gymId)) {
      return this.gymFinderService
        .getGym(+this.gymId)
        .pipe(tap(this.changeGymIdToUUID));
    }

    return of();
  }

  loadStudioTypes() {
    this.gymFinderService.getStudioTypes().subscribe({
      next: value => {
        this.studioTypes = value;
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
        this.openSnackBar('Could not load studio types');
      },
    });
  }

  private moveMainImageToTheFrontOfGymImages() {
    if (this.gym.mainImageId) {
      const index = this.gym.gymImages.findIndex(
        ({ imageId }) => imageId === this.gym.mainImageId
      );
      if (index > 0) {
        const mainImage = this.gym.gymImages.splice(index, 1);
        this.gym.gymImages.unshift(mainImage[0]);
      }
    }
  }

  saveGym() {
    this.moveMainImageToTheFrontOfGymImages();
    this.logger.info('GymDetailsComponent::saveGym', this.gym);

    if (!this.validateGym()) {
      return;
    }

    const gymTypesChanged = hasGymTypesChanged(
      this.gym.options?.gymTypes,
      this.originalGym?.options?.gymTypes
    );

    if (gymTypesChanged) {
      this.showNetworkChangeConfirmDialog();
    } else {
      this.performSave();
    }
  }

  private validateGym(): boolean {
    const { mainImageId, ...gym } = this.gym;

    if (gym.instagram && !GymUtils.isValidInstagramUsername(gym.instagram)) {
      this.openSnackBar('Invalid Instagram username');
      return false;
    }

    if (gym.website && !GymUtils.isValidHomePageURL(gym.website)) {
      this.openSnackBar('Invalid  Homepage URL');
      return false;
    }

    if (!gym.options?.activities?.length) {
      this.openSnackBar('Please select at least one activity');
      return false;
    }

    if (OpeningHourUtil.hasOpeningHoursV2Errors(gym.openingHoursV2)) {
      this.openSnackBar('Invalid opening hours: check time slots');
      return false;
    }

    if (
      !OpeningHourUtil.isValidAdditionalInfo(gym.openingHoursV2?.additionalInfo)
    ) {
      this.openSnackBar(
        'Opening hours explanation must contain at least one letter or number'
      );
      return false;
    }

    if (
      this.creditsFeatureFlag &&
      gym.options?.gymTypes?.includes(GymType.WELLPASS_FR_BE_CREDITS) &&
      !gym.creditsConfig?.pricing?.length
    ) {
      this.openSnackBar(
        'Credits Price is required when Credits membership type is selected'
      );
      return false;
    }

    if (this.gymForm?.invalid) {
      this.gymForm.form.markAllAsTouched();
      this.openSnackBar('Please fix invalid fields before saving');
      return false;
    }

    return true;
  }

  private showNetworkChangeConfirmDialog(): void {
    const dialogData: NetworkChangeDialogData = {
      previousGymTypes: this.originalGym?.options?.gymTypes ?? [],
      newGymTypes: this.gym.options?.gymTypes ?? [],
      classBookingEnabled: this.gym.classBookingConfig?.enabled ?? false,
    };

    const dialogRef = this.dialog.open(NetworkChangeConfirmDialogComponent, {
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.performSave();
      }
    });
  }

  private performSave(): void {
    const { mainImageId, ...gym } = this.gym;

    this.loading = true;
    this.gymFinderService.updateGym(gym).subscribe({
      next: gym => {
        this.loading = false;
        this.error = undefined;
        this.gym = GymUtils.validateAdditionalInfoForGym(gym);
        if (this.gym.gymImages?.length > 0) {
          this.gym.mainImageId = this.gym.gymImages[0].imageId;
        }
        this.originalGym = structuredClone(this.gym);
        this.gymSavedSubject.next(true);
        this.displayGymlibBackOfficeLink =
          this.gym.options?.gymTypes?.includes(
            GymType.WELLPASS_FR_BE_CREDITS
          ) ||
          this.gym.options?.gymTypes?.includes(
            GymType.WELLPASS_FR_BE_UNLIMITED
          );
        this.logger.info('GymDetailsComponent::saveGym result', this.gym);
        this.openSnackBar('Gym updated successfully!');
        this.updatePropagationInfo();
      },
      error: (e: HttpErrorResponse) => {
        this.error = e;
        this.loading = false;
        let errorMessage = 'Could not save gym';
        if (e.error.fieldErrors) {
          for (const error of e.error.fieldErrors) {
            errorMessage = `${errorMessage}; ${error.message}`;
          }
        }
        this.openSnackBar(errorMessage);
      },
    });
  }

  initTabs(gymId: string) {
    this.tabs = [
      {
        route: `/gym-location/gymfinder/gym/${gymId}/${InternalRoutes.DETAILS}`,
        label: 'Gym details',
        internalRoute: InternalRoutes.DETAILS,
      },
      {
        route: `/gym-location/gymfinder/gym/${gymId}/${InternalRoutes.IMAGES}`,
        label: 'Gym images',
        internalRoute: InternalRoutes.IMAGES,
      },
      {
        route: `/gym-location/gymfinder/gym/${gymId}/${InternalRoutes.CLASS_BOOKING}`,
        label: 'Access',
        internalRoute: InternalRoutes.CLASS_BOOKING,
      },
      {
        route: `/gym-location/gymfinder/gym/${gymId}/${InternalRoutes.CHECKIN_ATTEMPTS}`,
        label: 'MMS Checkin attempts',
        internalRoute: InternalRoutes.CHECKIN_ATTEMPTS,
      },
    ];
  }

  showGymDetailsJsonDialog() {
    const data: GymDetailsJsonDialogData = {
      gymDetails: this.gym,
    };
    this.dialog.open(GymDetailsJsonDialogComponent, { data });
  }

  syncGym() {
    const gymId = this.gym.gymId;
    if (!gymId) {
      return;
    }
    this.logger.info('GymComponent::syncGym for gym with ID', gymId);

    this.loading = true;
    this.gymFinderService.syncGym(gymId).subscribe({
      next: gym => {
        this.loading = false;
        this.error = undefined;
        this.gym = GymUtils.validateAdditionalInfoForGym(gym);
        this.openSnackBar('Gym synced successfully!');
        this.changeGymIdToUUID(this.gym);
        this.updatePropagationInfo();
      },
      error: e => {
        this.error = e;
        this.loading = false;
        const errorMessage = 'Unable to sync gym details';
        this.openSnackBar(errorMessage);
      },
    });
  }

  onAccessConfigChanged(data: {
    classBookingConfig: ClassBookingConfig;
    geofence?: number | null;
    tanAccountCreationEnabled: boolean;
  }) {
    this.gym.classBookingConfig = data.classBookingConfig;
    this.gym.geofence = data.geofence;
    this.gym.tanAccountCreationEnabled = data.tanAccountCreationEnabled;
    this.saveGym();
  }

  private changeGymIdToUUID(gym: Gym) {
    if (gym.gymUUID) {
      const routeSnapshot = this.route.snapshot;
      const upgradedURL = routeSnapshot.pathFromRoot
        .flatMap(p => p.url)
        .map(p => (+p.path == gym.gymId ? gym.gymUUID : p.path));

      this.router.navigate(upgradedURL, { replaceUrl: true }).then(value => {
        if (value && gym.gymUUID) {
          this.initTabs(gym.gymUUID);
          this.gymId = gym.gymUUID;
        }
      });
    }
  }

  protected readonly InternalRoutes = InternalRoutes;

  private updatePropagationInfo(): void {
    const timestampUpdate = this.gym?.timestampUpdate;

    if (timestampUpdate == null || isNaN(timestampUpdate)) {
      this.gymUpdatedAt = undefined;
      this.expectedLiveAt = undefined;
      this.isPropagationWindowElapsed = false;
      return;
    }

    const windowMs = GymComponent.PROPAGATION_WINDOW_MS;
    this.gymUpdatedAt = new Date(timestampUpdate);
    this.expectedLiveAt = new Date(timestampUpdate + windowMs);
    this.isPropagationWindowElapsed = Date.now() >= timestampUpdate + windowMs;
  }
}
